import { todayStr, toLocalDateStr } from "../../lib/date";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { downloadFromApi } from "../../lib/download";
import { migrateAutoOnes, getStudentsMissingDocuments, scanInvalidDna, cleanupInvalidDna, scanAutoCheckout, fixAutoCheckout, getDistrictAudit, setDistrictAudit, setDistrictAuditBulk, reviewTardyTimezone, fixTardyTimezone, getJobRuns, runNightlyNow } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import NewYearWizardDialog from "../NewYearWizard";
import ProgressArchiveReminder from "../ProgressArchiveReminder";
import DuplicatesFinder from "./DuplicatesFinder";
import DemoResetCard from "./DemoResetCard";
import HistoricalImportCard from "./HistoricalImportCard";
import IncidentImportCard from "./IncidentImportCard";
import HardcopyAccessAdmin from "./HardcopyAccessAdmin";

const EXPORT_SHEETS = [
  { key: "students", label: "Students" },
  { key: "staff", label: "Staff" },
  { key: "attendance", label: "Attendance" },
  { key: "assessments", label: "Assessments" },
  { key: "clinical_plans", label: "Clinical Plans" },
  { key: "student_services", label: "Student Services" },
  { key: "incidents", label: "Incident Reports" },
  { key: "calendar", label: "School Calendar" },
  { key: "communications", label: "Communications" },
  { key: "medical", label: "Medical & OTC Logs" },
  { key: "accidents", label: "Accident Reports" },
  { key: "drills", label: "Safety Drills" },
  { key: "risk", label: "Risk Assessments" },
  { key: "self_assessments", label: "Self-Assessments" },
  { key: "proficiency", label: "Proficiency Records" },
  { key: "services", label: "Services" },
  { key: "contacts", label: "Contacts" },
  { key: "districts", label: "Districts" },
  { key: "events", label: "Meetings & Events" },
  { key: "master_schedule", label: "Master Schedule" },
  { key: "signups", label: "Sign-Ups & Trips" },
  { key: "signup_rosters", label: "Sign-Up Rosters" },
];
const ALL_KEYS = EXPORT_SHEETS.map((s) => s.key);
// Curated subset most schools file with their state education agency.
const AOE_KEYS = ["students", "staff", "attendance", "assessments", "incidents", "clinical_plans", "services", "calendar", "medical", "accidents", "drills"];

// One-time retroactive DNA fix: converts legacy auto-filled "1's" records back
// into true DNA entries (removes fake low scores from historical averages).
function DnaFixCard() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (!window.confirm("Convert all legacy auto-1's assessment records back to DNA? This affects historical data and cannot be auto-undone.")) return;
    setRunning(true);
    try {
      const res = await migrateAutoOnes();
      setResult(res.data);
      toast.success(res.data.message);
    } catch { toast.error("Failed to run DNA fix"); }
    finally { setRunning(false); }
  };
  return (
    <Card data-testid="dna-fix-card">
      <CardHeader>
        <CardTitle>Fix Historical DNA (remove auto-1's)</CardTitle>
        <CardDescription>
          Earlier versions silently scored a DNA as all 1's, dragging averages down and hiding the DNA.
          This converts every such legacy record back to a true "Did Not Attend" (no scores). Run once.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button onClick={run} disabled={running} className="bg-orange-600 hover:bg-orange-700" data-testid="run-dna-fix-btn">
          {running ? "Converting…" : "Convert auto-1's → DNA"}
        </Button>
        {result && (
          <p className="text-sm text-emerald-700 font-semibold" data-testid="dna-fix-result">
            Converted {result.converted} record(s) back to DNA.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// Find & delete DNA records recorded for a block the student wasn't present for
// (absent all day / left early / alternate plan removed them from the block).
function InvalidDnaCard() {
  const [scanning, setScanning] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [data, setData] = useState(null);

  const scan = async () => {
    setScanning(true);
    try {
      const res = await scanInvalidDna();
      setData(res.data);
    } catch { toast.error("Failed to scan DNA records"); }
    finally { setScanning(false); }
  };

  const cleanup = async () => {
    if (!window.confirm("Delete all invalid DNA records (students who weren't present for the block)? This cannot be auto-undone.")) return;
    setDeleting(true);
    try {
      const res = await cleanupInvalidDna();
      toast.success(res.data.message);
      await scan();
    } catch { toast.error("Failed to clean up DNA records"); }
    finally { setDeleting(false); }
  };

  return (
    <Card data-testid="invalid-dna-card">
      <CardHeader>
        <CardTitle>Clean Up Invalid DNAs</CardTitle>
        <CardDescription>
          Finds "Did Not Attend" records logged for a block the student wasn't actually present for —
          they were absent all day, left early that day, or an alternate plan had them off-campus for that block.
          Scan to review, then delete so they stop dragging down DNA counts.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button onClick={scan} disabled={scanning} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="scan-invalid-dna-btn">
            {scanning ? "Scanning…" : "Scan for Invalid DNAs"}
          </Button>
          {data && data.invalid_count > 0 && (
            <Button onClick={cleanup} disabled={deleting} className="bg-orange-600 hover:bg-orange-700" data-testid="cleanup-invalid-dna-btn">
              {deleting ? "Deleting…" : `Delete ${data.invalid_count} Invalid DNA${data.invalid_count === 1 ? "" : "s"}`}
            </Button>
          )}
        </div>
        {data && (
          <div className="space-y-2" data-testid="invalid-dna-result">
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">{data.total_dna} DNA records</span>
              <span className={`px-3 py-1 rounded-full font-semibold ${data.invalid_count ? "bg-orange-100 text-orange-700" : "bg-emerald-100 text-emerald-700"}`} data-testid="invalid-dna-count">
                {data.invalid_count} invalid
              </span>
            </div>
            {data.invalid_count === 0 ? (
              <p className="text-xs text-emerald-700">No invalid DNA records — all DNAs belong to students who were present for the block.</p>
            ) : (
              <ul className="space-y-1 max-h-80 overflow-y-auto border rounded-lg p-2">
                {data.items.map((it) => (
                  <li key={it.id} className="flex items-center justify-between text-sm border-b last:border-0 py-1" data-testid={`invalid-dna-item-${it.id}`}>
                    <span>
                      <span className="font-semibold text-slate-900">{it.student_name}</span>
                      <span className="text-xs text-slate-500 ml-2">{it.date} · {it.block}{it.class_name ? ` · ${it.class_name}` : ""}</span>
                    </span>
                    <span className="text-xs font-medium text-orange-700">{it.reason}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// Find & correct PAST auto-checkout records that were closed at the old blanket
// time (flat 2:00 PM) instead of each student's planned departure.
function AutoCheckoutFixCard() {
  const [scanning, setScanning] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [data, setData] = useState(null);

  const scan = async () => {
    setScanning(true);
    try {
      const res = await scanAutoCheckout();
      setData(res.data);
    } catch { toast.error("Failed to scan auto-checkout records"); }
    finally { setScanning(false); }
  };

  const fix = async () => {
    if (!window.confirm("Correct all mis-closed auto-checkout records to each student's planned departure time? This updates historical attendance.")) return;
    setFixing(true);
    try {
      const res = await fixAutoCheckout();
      toast.success(res.data.message);
      await scan();
    } catch { toast.error("Failed to correct auto-checkout records"); }
    finally { setFixing(false); }
  };

  return (
    <Card data-testid="auto-checkout-fix-card">
      <CardHeader>
        <CardTitle>Fix Old Auto-Checkout Times</CardTitle>
        <CardDescription>
          Earlier versions auto-closed un-checked-out students at a blanket 2:00 PM. Now they close at each
          student's planned departure (scheduled end or alternate-plan time). Scan to find past records with the
          wrong checkout time, then correct them in one click.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button onClick={scan} disabled={scanning} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="scan-auto-checkout-btn">
            {scanning ? "Scanning…" : "Scan Auto-Checkout Times"}
          </Button>
          {data && data.fixable_count > 0 && (
            <Button onClick={fix} disabled={fixing} className="bg-orange-600 hover:bg-orange-700" data-testid="fix-auto-checkout-btn">
              {fixing ? "Correcting…" : `Correct ${data.fixable_count} Record${data.fixable_count === 1 ? "" : "s"}`}
            </Button>
          )}
        </div>
        {data && (
          <div className="space-y-2" data-testid="auto-checkout-result">
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">{data.total_auto} auto-checkout records</span>
              <span className={`px-3 py-1 rounded-full font-semibold ${data.fixable_count ? "bg-orange-100 text-orange-700" : "bg-emerald-100 text-emerald-700"}`} data-testid="auto-checkout-count">
                {data.fixable_count} to correct
              </span>
            </div>
            {data.fixable_count === 0 ? (
              <p className="text-xs text-emerald-700">All auto-checkout times already match each student's planned departure.</p>
            ) : (
              <ul className="space-y-1 max-h-80 overflow-y-auto border rounded-lg p-2">
                {data.items.map((it) => (
                  <li key={it.id} className="flex items-center justify-between text-sm border-b last:border-0 py-1 gap-2" data-testid={`auto-checkout-item-${it.id}`}>
                    <span className="min-w-0">
                      <span className="font-semibold text-slate-900">{it.student_name}</span>
                      <span className="text-xs text-slate-500 ml-2">{it.date}</span>
                    </span>
                    <span className="text-xs font-medium text-orange-700 whitespace-nowrap">
                      <span className="line-through text-slate-400 mr-1">{it.current_check_out}</span>
                      → {it.planned_check_out}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// Correct tardy check-ins that were saved in UTC instead of the school's local time.
function TardyTimezoneFixCard() {
  const [scanning, setScanning] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [data, setData] = useState(null);

  const scan = async () => {
    setScanning(true);
    try {
      const res = await reviewTardyTimezone();
      setData(res.data);
    } catch { toast.error("Failed to scan tardy times"); }
    finally { setScanning(false); }
  };

  const fix = async () => {
    if (!window.confirm("Correct all tardy records saved in UTC to the school's local time? This updates historical attendance.")) return;
    setFixing(true);
    try {
      const res = await fixTardyTimezone();
      toast.success(res.data.message);
      await scan();
    } catch { toast.error("Failed to correct tardy times"); }
    finally { setFixing(false); }
  };

  return (
    <Card data-testid="tardy-timezone-fix-card">
      <CardHeader>
        <CardTitle>Fix Tardy Times Saved in the Wrong Time Zone</CardTitle>
        <CardDescription>
          Earlier versions recorded a tardy check-in using the server's UTC clock, so morning tardies landed
          4–5 hours late (e.g. 9:15 AM saved as 1:15 PM). Scan to find those records, then correct each to your
          school's local time{data?.timezone ? ` (${data.timezone})` : ""} in one click.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button onClick={scan} disabled={scanning} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="scan-tardy-tz-btn">
            {scanning ? "Scanning…" : "Scan Tardy Times"}
          </Button>
          {data && data.fixable_count > 0 && (
            <Button onClick={fix} disabled={fixing} className="bg-orange-600 hover:bg-orange-700" data-testid="fix-tardy-tz-btn">
              {fixing ? "Correcting…" : `Correct ${data.fixable_count} Record${data.fixable_count === 1 ? "" : "s"}`}
            </Button>
          )}
        </div>
        {data && (
          <div className="space-y-2" data-testid="tardy-tz-result">
            <span className={`inline-block px-3 py-1 rounded-full font-semibold text-sm ${data.fixable_count ? "bg-orange-100 text-orange-700" : "bg-emerald-100 text-emerald-700"}`} data-testid="tardy-tz-count">
              {data.fixable_count} to correct
            </span>
            {data.fixable_count === 0 ? (
              <p className="text-xs text-emerald-700">No tardy records look like they were saved in UTC.</p>
            ) : (
              <ul className="space-y-1 max-h-80 overflow-y-auto border rounded-lg p-2">
                {data.items.map((it) => (
                  <li key={`${it.student_id}-${it.date}`} className="flex items-center justify-between text-sm border-b last:border-0 py-1 gap-2">
                    <span className="min-w-0">
                      <span className="font-semibold text-slate-900">{it.student_name}</span>
                      <span className="text-xs text-slate-500 ml-2">{it.date}</span>
                    </span>
                    <span className="text-xs font-medium text-orange-700 whitespace-nowrap">
                      <span className="line-through text-slate-400 mr-1">{it.current_check_in}</span>
                      → {it.corrected_check_in}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// Last run of each nightly maintenance job for THIS school, with a manual "Run now".
function ScheduledJobsCard() {
  const [runs, setRuns] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const res = await getJobRuns();
      setRuns(res.data.runs || []);
    } catch { toast.error("Failed to load scheduled jobs"); }
  };

  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const res = await runNightlyNow();
      setRuns(res.data.runs || []);
      toast.success("Nightly jobs ran for this school");
    } catch { toast.error("Failed to run nightly jobs"); }
    finally { setRunning(false); }
  };

  const fmt = (r) => {
    if (!r.last_run_local && !r.last_run_at) return "Never run";
    try { return new Date(r.last_run_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return r.last_run_at; }
  };

  return (
    <Card data-testid="scheduled-jobs-card">
      <CardHeader>
        <CardTitle>Nightly Maintenance Jobs</CardTitle>
        <CardDescription>
          These run automatically each night in your school's time zone{runs && runs[0]?.timezone ? ` (${runs[0].timezone})` : ""}.
          Here's the last run of each — you can also run them now.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button onClick={runNow} disabled={running} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="run-nightly-now-btn">
          {running ? "Running…" : "Run Nightly Jobs Now"}
        </Button>
        {runs && (
          <ul className="space-y-2" data-testid="job-runs-list">
            {runs.map((r) => (
              <li key={r.job} className="flex items-center justify-between text-sm border rounded-lg p-2" data-testid={`job-run-${r.job}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900">{r.label}</div>
                  <div className="text-xs text-slate-500">{r.detail || r.status}</div>
                </div>
                <div className="text-right whitespace-nowrap">
                  <div className="text-xs font-medium text-slate-700" data-testid={`job-run-time-${r.job}`}>{fmt(r)}</div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === "ok" ? "bg-emerald-100 text-emerald-700" : r.status === "never" ? "bg-slate-100 text-slate-500" : "bg-amber-100 text-amber-700"}`}>{r.status}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}



// Audit CURRENT students for required documents (IEP + Contract).
function MissingDocsCard() {
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(null);
  const run = async () => {
    setRunning(true);
    try {
      const res = await getStudentsMissingDocuments();
      setData(res.data);
    } catch { toast.error("Failed to run document check"); }
    finally { setRunning(false); }
  };

  const List = ({ title, rows, kind }) => (
    <div className="border rounded-lg p-3" data-testid={`missing-${kind}-section`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-semibold text-sm text-[#00305E]">{title}</h4>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700" data-testid={`missing-${kind}-count`}>{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-emerald-700">All current students have this.</p>
      ) : (
        <ul className="space-y-1 max-h-72 overflow-y-auto">
          {rows.map((r) => (
            <li key={r.id} className="flex items-center justify-between text-sm border-b last:border-0 py-1">
              <Link to={`/students/${r.id}`} className="text-blue-700 hover:underline" data-testid={`missing-${kind}-student-${r.id}`}>{r.name}</Link>
              <span className="text-xs text-muted-foreground">{r.grade ? `Grade ${r.grade}` : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <Card data-testid="missing-docs-card">
      <CardHeader>
        <CardTitle>Missing IEP / Contract</CardTitle>
        <CardDescription>
          Every current student should have an IEP and a Contract on file. This lists current students
          missing either (an IEP counts if they have an uploaded IEP document <strong>or</strong> an IEP Drive link).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={run} disabled={running} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="run-missing-docs-btn">
          {running ? "Checking…" : "Check Missing IEP / Contract"}
        </Button>
        {data && (
          <div className="space-y-4" data-testid="missing-docs-result">
            <div className="flex flex-wrap gap-4 text-sm">
              <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">{data.total_current} current students</span>
              <span className="px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold" data-testid="missing-docs-complete">{data.complete} complete</span>
              <span className="px-3 py-1 rounded-full bg-orange-100 text-orange-700 font-semibold">{data.missing_both.length} missing both</span>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <List title="Missing IEP" rows={data.missing_iep} kind="iep" />
              <List title="Missing Contract" rows={data.missing_contract} kind="contract" />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DistrictAuditCard() {
  const [running, setRunning] = useState(false);
  const [data, setData] = useState(null);
  const [picks, setPicks] = useState({});   // student_id -> selected district
  const [saving, setSaving] = useState({});  // student_id -> bool
  const [selected, setSelected] = useState(new Set()); // student_ids chosen for bulk
  const [bulkDistrict, setBulkDistrict] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const run = async () => {
    setRunning(true);
    try {
      const res = await getDistrictAudit();
      setData(res.data);
      const seed = {};
      (res.data.issues || []).forEach((i) => { if (i.suggestion) seed[i.id] = i.suggestion; });
      setPicks(seed);
      setSelected(new Set());
      setBulkDistrict("");
    } catch { toast.error("Failed to run district check"); }
    finally { setRunning(false); }
  };

  const dropRows = (ids) => {
    const idSet = ids instanceof Set ? ids : new Set(ids);
    setData((prev) => {
      if (!prev) return prev;
      const issues = prev.issues.filter((i) => !idSet.has(i.id));
      return {
        ...prev,
        issues,
        counts: {
          ...prev.counts,
          missing: issues.filter((i) => i.status === "missing").length,
          unmatched: issues.filter((i) => i.status === "unmatched").length,
        },
      };
    });
    setSelected((prev) => { const n = new Set(prev); idSet.forEach((id) => n.delete(id)); return n; });
  };

  const save = async (row) => {
    const district = picks[row.id];
    if (!district) { toast.error("Pick a district first"); return; }
    setSaving((p) => ({ ...p, [row.id]: true }));
    try {
      await setDistrictAudit(row.id, district);
      toast.success(`${row.name} → ${district}`);
      dropRows([row.id]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to set district");
    } finally {
      setSaving((p) => ({ ...p, [row.id]: false }));
    }
  };

  const toggleSel = (id) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const selectAll = () => setSelected(new Set((data?.issues || []).map((i) => i.id)));
  const selectMissing = () => setSelected(new Set((data?.issues || []).filter((i) => i.status === "missing").map((i) => i.id)));
  const clearSel = () => setSelected(new Set());

  const bulkSave = async () => {
    if (!bulkDistrict) { toast.error("Pick a district for the selected students"); return; }
    const ids = Array.from(selected);
    if (ids.length === 0) { toast.error("Select at least one student"); return; }
    setBulkSaving(true);
    try {
      const res = await setDistrictAuditBulk(ids, bulkDistrict);
      toast.success(`Set ${res.data?.updated ?? ids.length} student(s) → ${bulkDistrict}`);
      dropRows(ids);
      setBulkDistrict("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to bulk-set district");
    } finally { setBulkSaving(false); }
  };

  const TYPE_BADGE = {
    current: "bg-emerald-100 text-emerald-700",
    former: "bg-slate-200 text-slate-700",
    graduated: "bg-purple-100 text-purple-700",
    referral: "bg-orange-100 text-orange-700",
  };

  return (
    <Card data-testid="district-audit-card">
      <CardHeader>
        <CardTitle>Sending-School District Check</CardTitle>
        <CardDescription>
          Finds students (current, former, graduated, and referrals) whose Sending-School district is
          <strong> missing</strong> or doesn't <strong>match</strong> a name in your Sending Schools list.
          Set the correct district one at a time, or select several and set them together.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button onClick={run} disabled={running} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="run-district-audit-btn">
          {running ? "Checking…" : "Check Sending-School Districts"}
        </Button>

        {data && (
          <div className="space-y-3" data-testid="district-audit-result">
            <div className="flex flex-wrap gap-3 text-sm">
              <span className="px-3 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">{data.counts.total_students} students scanned</span>
              <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 font-semibold" data-testid="district-audit-missing-count">{data.counts.missing} missing</span>
              <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 font-semibold" data-testid="district-audit-unmatched-count">{data.counts.unmatched} not matching</span>
            </div>

            {data.issues.length === 0 ? (
              <p className="text-sm text-emerald-700" data-testid="district-audit-clean">Every student has a valid Sending-School district. 🎉</p>
            ) : (
              <>
                {/* Bulk action bar */}
                <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-slate-50 p-2" data-testid="district-audit-bulk-bar">
                  <div className="flex items-center gap-1 text-xs">
                    <button type="button" onClick={selectAll} className="px-2 py-1 rounded border hover:bg-white" data-testid="district-audit-select-all">Select all</button>
                    <button type="button" onClick={selectMissing} className="px-2 py-1 rounded border hover:bg-white" data-testid="district-audit-select-missing">Select missing</button>
                    <button type="button" onClick={clearSel} className="px-2 py-1 rounded border hover:bg-white" data-testid="district-audit-clear-sel">Clear</button>
                  </div>
                  <span className="text-xs text-muted-foreground" data-testid="district-audit-selected-count">{selected.size} selected</span>
                  <div className="flex items-center gap-2 ml-auto">
                    <Select value={bulkDistrict} onValueChange={setBulkDistrict}>
                      <SelectTrigger className="w-56 h-9" data-testid="district-audit-bulk-select">
                        <SelectValue placeholder="District for selected…" />
                      </SelectTrigger>
                      <SelectContent>
                        {data.districts.map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" onClick={bulkSave} disabled={bulkSaving || selected.size === 0 || !bulkDistrict} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="district-audit-bulk-save">
                      {bulkSaving ? "Setting…" : `Set for ${selected.size} selected`}
                    </Button>
                  </div>
                </div>

                <div className="border rounded-lg divide-y max-h-[32rem] overflow-y-auto">
                  {data.issues.map((row) => (
                    <div key={row.id} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3" data-testid={`district-audit-row-${row.id}`}>
                      <Checkbox checked={selected.has(row.id)} onCheckedChange={() => toggleSel(row.id)} data-testid={`district-audit-check-${row.id}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <Link to={`/students/${row.id}`} className="text-blue-700 hover:underline font-medium truncate">{row.name}</Link>
                          <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${TYPE_BADGE[row.type] || "bg-slate-100 text-slate-600"}`}>{row.type}</span>
                        </div>
                        <div className="text-xs mt-0.5">
                          {row.status === "missing" ? (
                            <span className="text-red-600 font-medium">No district listed</span>
                          ) : (
                            <span className="text-amber-700">Current value: <span className="font-medium">"{row.current_value}"</span> — not in the list</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select value={picks[row.id] || ""} onValueChange={(v) => setPicks((p) => ({ ...p, [row.id]: v }))}>
                          <SelectTrigger className="w-56 h-9" data-testid={`district-audit-select-${row.id}`}>
                            <SelectValue placeholder="Pick a district…" />
                          </SelectTrigger>
                          <SelectContent>
                            {data.districts.map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
                          </SelectContent>
                        </Select>
                        <Button size="sm" onClick={() => save(row)} disabled={!picks[row.id] || saving[row.id]} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`district-audit-save-${row.id}`}>
                          {saving[row.id] ? "Saving…" : "Set"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function DataManagementTab() {
  const [exporting, setExporting] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(() => {
    const d = new Date(); d.setMonth(d.getMonth() - 12);
    return toLocalDateStr(d);
  });
  const [exportEndDate, setExportEndDate] = useState(todayStr());
  const [selectedSheets, setSelectedSheets] = useState(() => new Set(ALL_KEYS));
  const [wizardOpen, setWizardOpen] = useState(false);

  const toggleSheet = (key) => setSelectedSheets((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const handleExport = async () => {
    if (selectedSheets.size === 0) { toast.error("Select at least one data type to export"); return; }
    setExporting(true);
    try {
      const isAll = selectedSheets.size === ALL_KEYS.length;
      let url = `/api/export/all-data?start_date=${exportStartDate}&end_date=${exportEndDate}`;
      if (!isAll) url += `&sheets=${Array.from(selectedSheets).join(",")}`;
      const tag = isAll ? "Full" : "Scoped";
      await downloadFromApi(url, `TMS_${tag}_Data_Export_${exportStartDate}_to_${exportEndDate}.xlsx`);
      toast.success("Data export downloaded successfully");
    } catch { toast.error("Failed to export data"); } finally { setExporting(false); }
  };

  return (
    <Tabs defaultValue="export" className="space-y-6" data-testid="data-management-tabs">
      <TabsList className="flex flex-wrap gap-1 h-auto p-1">
        <TabsTrigger value="export" data-testid="dm-tab-export">Export</TabsTrigger>
        <TabsTrigger value="transition" data-testid="dm-tab-transition">Year Transition</TabsTrigger>
        <TabsTrigger value="import" data-testid="dm-tab-import">Import</TabsTrigger>
        <TabsTrigger value="tools" data-testid="dm-tab-tools">Data Tools</TabsTrigger>
        <TabsTrigger value="hardcopy" data-testid="dm-tab-hardcopy">Hard Copy Access</TabsTrigger>
      </TabsList>

      <TabsContent value="export" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Annual Data Export</CardTitle>
          <CardDescription>Download all school data for AOE filing and record keeping</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="p-6 border-2 border-dashed rounded-lg bg-muted/30">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#00305E] flex items-center justify-center text-white">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg">Full Data Export</h3>
                <p className="text-muted-foreground mt-1 mb-3">Download a comprehensive Excel workbook containing school data for the selected date range.</p>
                <div className="flex items-end gap-3 mb-3">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">Start Date</label>
                    <input type="date" value={exportStartDate} onChange={e => setExportStartDate(e.target.value)}
                      className="block w-40 rounded-md border border-input bg-background px-3 py-1.5 text-sm" data-testid="export-start-date" />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-muted-foreground">End Date</label>
                    <input type="date" value={exportEndDate} onChange={e => setExportEndDate(e.target.value)}
                      className="block w-40 rounded-md border border-input bg-background px-3 py-1.5 text-sm" data-testid="export-end-date" />
                  </div>
                </div>

                <div className="mb-3">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-muted-foreground">Include:</span>
                    <button type="button" onClick={() => setSelectedSheets(new Set(ALL_KEYS))}
                      className="px-2.5 py-1 rounded-full text-xs font-medium bg-[#00305E] text-white hover:bg-[#00305E]/90" data-testid="export-preset-full">
                      Full backup (all)
                    </button>
                    <button type="button" onClick={() => setSelectedSheets(new Set(AOE_KEYS))}
                      className="px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700" data-testid="export-preset-aoe">
                      AOE filing only
                    </button>
                    <button type="button" onClick={() => setSelectedSheets(new Set())}
                      className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200" data-testid="export-preset-clear">
                      Clear
                    </button>
                    <span className="text-xs text-muted-foreground ml-auto" data-testid="export-selected-count">
                      {selectedSheets.size} of {ALL_KEYS.length} selected
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 rounded-lg border border-input p-3 max-h-52 overflow-y-auto bg-background">
                    {EXPORT_SHEETS.map((s) => (
                      <label key={s.key} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                        <input type="checkbox" checked={selectedSheets.has(s.key)} onChange={() => toggleSheet(s.key)}
                          className="h-4 w-4 rounded border-input accent-[#00305E]" data-testid={`export-sheet-${s.key}`} />
                        <span className="truncate">{s.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <Button onClick={handleExport} disabled={exporting} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="export-all-data-btn">
                  {exporting ? "Exporting..." : "Download Data Export"}
                </Button>
              </div>
            </div>
          </div>
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <h4 className="font-medium text-amber-800">Important Notes</h4>
            <ul className="text-sm text-amber-700 mt-1 space-y-1">
              <li>- This export contains sensitive student information - handle with care</li>
              <li>- Store exported files securely according to FERPA guidelines</li>
            </ul>
          </div>
        </CardContent>
      </Card>
      </TabsContent>

      <TabsContent value="transition" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>School Year Transition</CardTitle>
          <CardDescription>Reset attendance between semesters, or run the full end-of-year transition</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
            <h4 className="font-semibold text-teal-800">Semesters (date-filter based)</h4>
            <p className="text-sm text-teal-700 mt-1 mb-3"><strong>Your data is never deleted.</strong> All attendance stays in one place. To keep current percentages and reports clean, set the <strong>date range</strong> on any report (e.g. Fall 2026) to view just that semester. Nothing needs to be &quot;reset.&quot;</p>
            <div className="mt-4"><ProgressArchiveReminder /></div>
          </div>
          <div className="p-4 bg-[#00305E]/5 border border-[#00305E]/20 rounded-lg">
            <h4 className="font-semibold text-[#00305E]">End of Year Transition (New Year Wizard)</h4>
            <p className="text-sm text-muted-foreground mt-1 mb-3">Run at the end of the school year. A guided process that advances returning students one grade level and lets you mark graduates and former students. It saves a snapshot of the year to the Archives page — <strong>no records are ever deleted</strong>; every attendance, assessment, and incident stays in student files.</p>
            <Button className="bg-[#00305E] hover:bg-[#00305E]/90" onClick={() => setWizardOpen(true)} data-testid="open-wizard-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2"><path d="m21.64 3.64-1.28-1.28a1.21 1.21 0 0 0-1.72 0L2.36 18.64a1.21 1.21 0 0 0 0 1.72l1.28 1.28a1.2 1.2 0 0 0 1.72 0L21.64 5.36a1.2 1.2 0 0 0 0-1.72Z"/><path d="m14 7 3 3"/><path d="M5 6v4"/><path d="M19 14v4"/><path d="M10 2v2"/><path d="M7 8H3"/><path d="M21 16h-4"/><path d="M11 3H9"/></svg>
              Launch New Year Wizard
            </Button>
          </div>
        </CardContent>
      </Card>

      <NewYearWizardDialog open={wizardOpen} onOpenChange={setWizardOpen} />
      </TabsContent>

      <TabsContent value="import" className="space-y-6">
        <HistoricalImportCard />
        <IncidentImportCard />
      </TabsContent>

      <TabsContent value="tools" className="space-y-6">
        <MissingDocsCard />
        <DistrictAuditCard />
        <DnaFixCard />
        <InvalidDnaCard />
        <AutoCheckoutFixCard />
        <TardyTimezoneFixCard />
        <ScheduledJobsCard />
        <DuplicatesFinder />
        <DemoResetCard />
      </TabsContent>

      <TabsContent value="hardcopy" className="space-y-6">
        <HardcopyAccessAdmin />
      </TabsContent>
    </Tabs>
  );
}
